"use client";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks";
import { useAuthStore } from "@/store/use-auth-store";
import { Link, useNavigate } from "react-router-dom";
import { PlusIcon } from "./icons";
import { SidebarHistory } from "./sidebar-history";

interface AppSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function AppSidebar({ isOpen, onToggle }: AppSidebarProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isMobile = useIsMobile();

  // Mobile: Use Sheet (drawer)
  if (isMobile) {
    return (
      <TooltipProvider>
        <Sheet open={isOpen} onOpenChange={onToggle}>
          <SheetContent
            side="right"
            className="w-80 p-0 transition-all duration-300 ease-in-out [&>button]:hidden"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>AI Chat History</SheetTitle>
            </SheetHeader>
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between h-16 px-4 border-b">
                <Link
                  to="#"
                  onClick={() => onToggle()}
                  className="flex items-center gap-3"
                >
                  <span className="text-lg font-semibold hover:bg-muted rounded-md px-2 py-1 cursor-pointer">
                    Uza-AI
                  </span>
                </Link>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        onToggle();
                        navigate("/chat");
                      }}
                    >
                      <PlusIcon />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>New Chat</TooltipContent>
                </Tooltip>
              </div>
              <div className="flex-1 overflow-y-auto">
                <SidebarHistory />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    );
  }

  // Desktop: Use flex layout for push behavior with animations
  return (
    <TooltipProvider>
      <div
        className={`h-full bg-white border-l transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "w-80 opacity-100" : "w-0 opacity-0"
        }`}
      >
        <div className="flex h-full flex-col w-80 min-w-80">
          <div className="flex items-center justify-between h-16 px-4 border-b">
            <Link
              to="#"
              onClick={() => onToggle()}
              className="flex items-center gap-3"
            >
              <span className="text-lg font-semibold hover:bg-muted rounded-md px-2 py-1 cursor-pointer">
                Uza-AI
              </span>
            </Link>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    onToggle();
                    navigate("/chat");
                  }}
                >
                  <PlusIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent>New Chat</TooltipContent>
            </Tooltip>
          </div>
          <div className="flex-1 overflow-y-auto">
            <SidebarHistory />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
